import { FormikProps } from 'formik';
import { forEach, isEmpty, sumBy, uniqBy } from 'lodash';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components/macro';
import { useTemporaryBoolean } from '../../../helpers/hooks/useTemporaryBoolean';
import { Alert } from '../../../shared/Alert';
import { PrimaryProgressButton } from '../../../shared/Button';
import { FadingMessage } from '../../../shared/FadingMessage';
import { Form, FormButtonRow, FormHeaderFieldsContainer, TopLevelThermostatContainer } from '../../../shared/form/Form';
import { CheckboxField } from '../../../shared/form/inputs/CheckboxField';
import { getNullSelectOption, SelectOptions } from '../../../shared/form/inputs/Select';
import { SelectField } from '../../../shared/form/inputs/SelectField';
import { TextInputField } from '../../../shared/form/inputs/TextInputField';
import { ReadOnlyField } from '../../../shared/form/ReadOnlyField';
import {
  TopLevelValidationError,
  TopLevelValidationErrorContainer,
  topLevelValidationErrorTestId,
} from '../../../shared/form/TopLevelValidationError';
import { spacing03 } from '../../../styling/spacing';
import { Project } from '../../projects/project-section/ProjectHeader';
import { AnimateInContextProvider } from '../../shared/quote-or-design-form/AnimateInContext';
import { DataForQuoteOrDesignForm } from '../../shared/quote-or-design-form/GetDataForQuoteOrDesignForm';
import {
  QuoteOrDesignFormContext,
  QuoteOrDesignFormContextValue,
} from '../../shared/quote-or-design-form/QuoteOrDesignFormContext';
import {
  nonSharedThermostatSelectOptions,
  ThermostatTypeCode,
} from '../../shared/quote-or-design-form/quoteOrDesignFormData';
import { manifoldFieldName, roomFieldName } from '../../shared/quote-or-design-form/quoteOrDesignFormFieldNameHelpers';
import { UnitsForm } from '../../shared/quote-or-design-form/units/UnitsForm';
import { QuoteFormModel } from './quoteFormModel';

export type NextStepAfterSavingQuote = 'BackToProject' | 'GenerateBillOfMaterials';

type Props = FormikProps<QuoteFormModel> & {
  project: Project;
  dataForQuoteOrDesignForm: DataForQuoteOrDesignForm;
  disabled?: boolean;
  setNextStepAfterSave: (nextStep: NextStepAfterSavingQuote) => void;
};

export const QuoteForm = (props: Props) => {
  const isInReadOnlyMode = props.disabled;

  const customers = uniqBy([props.project.customer, ...props.project.alternativeCustomers], 'customerId');
  const quoteHasMultipleCustomers = customers.length > 1;

  const customerIdSelectOptions: SelectOptions<number | null> = customers.map(customer => ({
    text: `${customer.name} (${customer.customerReference})`,
    value: customer.customerId,
  }));
  if (quoteHasMultipleCustomers) {
    customerIdSelectOptions.unshift(getNullSelectOption<number>());
  }

  const [hasAttemptedToSubmit, setHasAttemptedToSubmit] = useState(false);

  const handleSaveClick = (nextStep: NextStepAfterSavingQuote) => {
    props.setNextStepAfterSave(nextStep);
    setHasAttemptedToSubmit(true);
  };

  const [isShowingSavedAsDraftMessage, showSavedAsDraftMessage] = useTemporaryBoolean();
  useEffect(() => {
    if (!isShowingSavedAsDraftMessage && props.status === 'Saved as draft') {
      showSavedAsDraftMessage();
    }
  }, [props.status, isShowingSavedAsDraftMessage, showSavedAsDraftMessage]);

  const quoteFormContextValue: QuoteOrDesignFormContextValue = {
    formType: 'Quote',
    buildTypeCode: props.project.buildTypeCode,
    externalAirTemperatureInDegreesCelsius: props.project.externalAirTemperatureInDegreesCelsius,
    referenceData: props.dataForQuoteOrDesignForm,
    formProps: props,
  };

  const sumOfUnitQuantities = sumBy(props.values.units, unit =>
    isNaN(Number(unit.quantity)) ? 0 : Number(unit.quantity),
  );

  const handleThermostatChange = (newThermostatTypeCode: ThermostatTypeCode) => {
    forEach(props.values.units, (unit, unitIndex) => {
      forEach(unit.manifolds, (manifold, manifoldIndex) => {
        forEach(manifold.rooms, (room, roomIndex) => {
          if (room.thermostatTypeCode === props.values.thermostatTypeCode) {
            props.setFieldValue(
              roomFieldName(unitIndex, manifoldIndex, roomIndex, 'thermostatTypeCode'),
              newThermostatTypeCode,
            );
          }
        });

        if (manifold.roomlessManifold?.thermostatTypeCode === props.values.thermostatTypeCode) {
          props.setFieldValue(
            manifoldFieldName(unitIndex, manifoldIndex, 'roomlessManifold.thermostatTypeCode'),
            newThermostatTypeCode,
          );
        }
      });
    });
  };

  return (
    <QuoteOrDesignFormContext.Provider value={quoteFormContextValue}>
      <Form disabled={props.disabled}>
        <FormHeaderFieldsContainer>
          <SelectField
            name="useLegacyPricingModel"
            label="Pricing Model"
            options={[
              { text: 'Old (Legacy)', value: true },
              { text: 'New', value: false },
            ]}
          />
          <TextInputField name="name" label="Quote Title" />
          <SelectField
            name="customerId"
            label="Customer"
            options={customerIdSelectOptions}
            disabled={!quoteHasMultipleCustomers}
          />
          <TopLevelThermostatContainer>
            <SelectField
              name="thermostatTypeCode"
              label="Thermostat"
              options={nonSharedThermostatSelectOptions}
              onChange={handleThermostatChange}
            />
            <CheckboxField name="includeNeoHub" label="Include neoHub" />
          </TopLevelThermostatContainer>
        </FormHeaderFieldsContainer>

        <AnimateInContextProvider>
          <UnitsForm units={props.values.units} />
        </AnimateInContextProvider>

        {sumOfUnitQuantities > 1 && (
          <FormHeaderFieldsContainer>
            <ReadOnlyField label="Total units" value={sumOfUnitQuantities.toString()} />
          </FormHeaderFieldsContainer>
        )}

        {!isInReadOnlyMode && <CheckboxField name="isDraft" label="Save as draft" />}

        {!isInReadOnlyMode && hasAttemptedToSubmit && (
          <>
            <TopLevelValidationError topLevelValidationFieldName="unitsValidation" formErrors={props.errors} />
            {!isEmpty(props.errors) && (
              <TopLevelValidationErrorContainer>
                <Alert
                  data-testid={topLevelValidationErrorTestId}
                  type="warning"
                  body="Some fields above are invalid - please correct them before attempting to submit."
                />
              </TopLevelValidationErrorContainer>
            )}
          </>
        )}

        <FormButtonRow $align="left">
          {!props.disabled && (
            <>
              <PrimaryProgressButton
                type="submit"
                inProgress={props.isSubmitting}
                onClick={() => handleSaveClick('BackToProject')}
              >
                {props.values.isDraft ? 'Save as Draft' : 'Save'}
              </PrimaryProgressButton>
              {!props.values.isDraft && (
                <PrimaryProgressButton
                  type="submit"
                  inProgress={props.isSubmitting}
                  onClick={() => handleSaveClick('GenerateBillOfMaterials')}
                >
                  Save and Generate BOM
                </PrimaryProgressButton>
              )}
              <SavedAsDraftMessage $visible={isShowingSavedAsDraftMessage}>Saved as draft</SavedAsDraftMessage>
            </>
          )}
        </FormButtonRow>
      </Form>
    </QuoteOrDesignFormContext.Provider>
  );
};

const SavedAsDraftMessage = styled(FadingMessage)`
  margin-left: ${spacing03};
`;
